import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  Grid,
  Tooltip,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { editImageWithOpenAI } from '../../utils/openaiUtils';

const ImageEditDialog = ({ open, onClose, imageUrl, onImageEdited }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editedImages, setEditedImages] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState('vibrant');

  // Preset styles for quick editing
  const presetStyles = [
    { id: 'vibrant', label: 'More Vibrant', prompt: 'Make the image more vibrant, colorful and eye-catching for social media advertising' },
    { id: 'professional', label: 'Professional', prompt: 'Make the image look more professional, clean and corporate' },
    { id: 'modern', label: 'Modern', prompt: 'Give the image a modern, contemporary look with trendy design elements' },
    { id: 'warm', label: 'Warm & Friendly', prompt: 'Make the image warmer and more inviting with friendly colors' },
    { id: 'high-contrast', label: 'High Contrast', prompt: 'Increase contrast and make text/elements stand out more' },
    { id: 'custom', label: 'Custom', prompt: '' }
  ];

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setPrompt('');
      setError(null);
      setEditedImages([]);
      setSelectedStyle('vibrant');
    } else {
      // Set initial prompt based on selected style
      const style = presetStyles.find(s => s.id === 'vibrant');
      setPrompt(style.prompt);
    }
  }, [open]);

  const handleEditImage = async () => {
    if (!prompt.trim()) {
      setError('Please enter a description of the changes you want to make');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await editImageWithOpenAI(
        imageUrl,
        prompt.trim(),
        null, // No mask for now
        '1024x1024',
        2, // Generate 2 variations
        'gpt-image-1-mini' // Use gpt-image-1-mini model
      );

      // Handle both single image and multiple images response
      const images = Array.isArray(result) ? result : [result];
      setEditedImages(images);

    } catch (err) {
      console.error('Error editing image:', err);
      setError(err.message || 'Failed to edit image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEditedImage = (editedImageUrl) => {
    onImageEdited(editedImageUrl);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoFixHighIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Create Image Variations with AI</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Original Image */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Original Image
            </Typography>
            <Card elevation={2}>
              <CardMedia
                component="img"
                image={imageUrl}
                alt="Original image"
                sx={{ 
                  maxHeight: 300,
                  objectFit: 'contain',
                  backgroundColor: '#f5f5f5'
                }}
              />
            </Card>
          </Grid>

          {/* Edit Controls */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Describe Your Changes
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="What changes would you like to make?"
              placeholder="e.g., Make the colors more vibrant, add a professional background, improve lighting, make it look more modern..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="Describe the specific changes or improvements you want to make to the image"
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                💡 <strong>Tips for better results:</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                • Be specific about colors, style, or mood changes
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                • Mention if you want to add/remove elements
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                • Describe the desired atmosphere or feeling
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleEditImage}
              disabled={loading || !prompt.trim()}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ mb: 2 }}
            >
              {loading ? 'Editing Image...' : 'Edit Image with AI'}
            </Button>

            {loading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  This may take 10-30 seconds...
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Edited Images Results */}
          {editedImages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Edited Images (Click to Select)
              </Typography>
              <Grid container spacing={2}>
                {editedImages.map((image, index) => (
                  <Grid item xs={6} md={4} key={index}>
                    <Tooltip title="Click to use this edited image">
                      <Card 
                        elevation={2}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            elevation: 4,
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => handleSelectEditedImage(image.url)}
                      >
                        <CardMedia
                          component="img"
                          image={image.url}
                          alt={`Edited variation ${index + 1}`}
                          sx={{ 
                            height: 200,
                            objectFit: 'contain',
                            backgroundColor: '#f5f5f5'
                          }}
                        />
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="primary">
                            Variation {index + 1}
                          </Typography>
                        </Box>
                      </Card>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageEditDialog;